/* eslint-disable import/prefer-default-export */
/* eslint-disable camelcase */
import {
    ARCHITECTURE_1oo1,
    ARCHITECTURE_1oo2,
    ARCHITECTURE_1oo2D,
    ARCHITECTURE_1oo3,
    ARCHITECTURE_2oo2,
    ARCHITECTURE_2oo3,
    PFD,
    PFH,
} from "const"
import {
    SilCalculationType,
    SilArchitecture,
} from "client/utils"


interface Iec_61508_2010 {
    calcType: SilCalculationType
    architecture: SilArchitecture
    mttr: number
    mrt: number
    beta: number
    betaD: number
    t1: number
    k: number
    sd: number
    dd: number
    du: number
}

// "**" (double "*") is an equivalent of "Math.pow"
export const iec_61508_2010_PFD = ({
    calcType,
    architecture,
    mttr,
    mrt,
    beta,
    betaD,
    t1,
    k,
    sd,
    dd,
    du,
}: Iec_61508_2010) => {
    const d = du + dd
    const tce = TCE({ mttr, mrt, t1, du, dd, d })
    const tge = TGE({ mttr, mrt, t1, du, dd, d })

    if (architecture === ARCHITECTURE_1oo1) {
        if (calcType === PFD) {
            return (du + dd) * tce
        }
        if (calcType === PFH) {
            return du
        }
    }

    if (architecture === ARCHITECTURE_1oo2) {
        const rt = (1 - betaD) * dd + (1 - beta) * du
        if (calcType === PFD) {
            return 2 * (rt ** 2) * tce * tge + betaD * dd * mttr + beta * du * (t1 / 2 + mrt)
        }
        if (calcType === PFH) {
            return 2 * rt * (1 - beta) * du * tce + beta * du
        }
    }

    if (architecture === ARCHITECTURE_2oo2) {
        if (calcType === PFD) {
            return 2 * d * tce
        }
        if (calcType === PFH) {
            return 2 * du
        }
    }

    if (architecture === ARCHITECTURE_1oo2D) {
        const tcePrime = TCEPrime({ mttr, mrt, t1, sd, dd, du })
        const tgePrime = TGEPrime({ mrt, t1 })

        const rt = 2 * (1 - beta) * du * ((1 - beta) * du + (1 - betaD) * dd + sd)
        if (calcType === PFD) {
            return rt * tcePrime * tgePrime + 2 * (1 - k) * dd * tcePrime + beta * du * (t1 / 2 + mrt)
        }
        if (calcType === PFH) {
            return rt * tcePrime + 2 * (1 - k) * dd + beta * du
        }
    }

    if (architecture === ARCHITECTURE_2oo3) {
        const rt = (1 - betaD) * dd + (1 - beta) * du
        if (calcType === PFD) {
            return 6 * (rt ** 2) * tce * tge + betaD * dd * mttr + beta * du * (t1 / 2 + mrt)
        }
        if (calcType === PFH) {
            return 6 * rt * (1 - beta) * du * tce + beta * du
        }
    }

    if (architecture === ARCHITECTURE_1oo3) {
        const tg2e = TG2E({ mttr, mrt, t1, dd, du, d })
        const rt = (1 - betaD) * dd + (1 - beta) * du
        if (calcType === PFD) {
            return 6 * (rt ** 3) * tce * tge * tg2e + betaD * dd * mttr + beta * du * (t1 / 2 + mrt)
        }
        if (calcType === PFH) {
            return 6 * (rt ** 2) * (1 - beta) * du * tce * tge + beta * du
        }
    }

    return null
}


// used functions
interface TCEProps {
    mttr: number
    mrt: number
    t1: number
    du: number
    dd: number
    d: number
}
const TCE = ({ mttr, mrt, t1, du, dd, d }: TCEProps) => {
    const tce1 = t1 / 2 + mrt
    return (du / d) * tce1 + (dd / d) * mttr
}


interface TGEProps {
    mttr: number
    mrt: number
    t1: number
    du: number
    dd: number
    d: number
}
const TGE = ({ mttr, mrt, t1, du, dd, d }: TGEProps) => {
    const tge1 = t1 / 3 + mrt
    return (du / d) * tge1 + (dd / d) * mttr
}


interface TCEPrimeProps {
    mttr: number
    mrt: number
    t1: number
    sd: number
    dd: number
    du: number
}
const TCEPrime = ({ mttr, mrt, t1, sd, dd, du }: TCEPrimeProps) => {
    const tce1 = t1 / 2 + mrt
    const tce2 = dd + sd
    const tce3 = du + dd + sd
    return (du * tce1 + tce2 * mttr) / tce3
}


interface TGEPrimeProps {
    mrt: number
    t1: number
}
const TGEPrime = ({ mrt, t1 }: TGEPrimeProps) => (
    t1 / 3 + mrt
)


interface TG2EProps {
    mttr: number
    mrt: number
    t1: number
    du: number
    dd: number
    d: number
}
const TG2E = ({ mttr, mrt, t1, dd, du, d }: TG2EProps) => {
    const tge1 = t1 / 4 + mrt
    return (du / d) * tge1 + (dd / d) * mttr
}
